import { Suspense, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Outlet } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PageLoader } from '@customerio/pluma-components/react';
import { ThemeContextProvider } from '@/context/theme';
import Authenticated from '@/layouts/authenticated';
import DesignStudioPage from '@/pages/design-studio.page';
import NotFoundPage from '@/pages/not-found.page';
import { handleWorkspaceTransition } from '@/services/db/shared/workspace-cache-sync';
import { useLocation } from '@/services/router/compat';
import { getWorkspaceIdParam } from '@/services/router/workspace-id-param';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useStore } from '@nanostores/react';
import { accountStore } from '@/services/auth/store';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { appEntryGateDestination } from './-app-entry-gate';
import { useAppEntryRevalidation } from './-app-entry-revalidation';
import { Navigate } from '@/utils/navigate';
import { AddonPurchaseBridge, AssetLibrary } from './-route-pages';
import { LastWorkspaceResolver } from './-route-helpers';

// Kept here for importers that predate -route-helpers. Import them from there: this module loads the
// whole authenticated layout, which a route wrapper or page test only needing these does not want.
export { LastWorkspaceResolver, LazyPage } from './-route-helpers';

/**
 * The redirect an app-entry gate renders, choosing the navigation primitive by what serves the URL the
 * account *arrived* on.
 *
 * A gate renders this **instead of** the app shell, so the embedded app is not mounted while the
 * redirect happens. `SafeNavigate` routes an embedded-bundle transition through the child-app
 * load/confirm handshake and parks it until `childAppLoaded` turns true — which cannot happen when the
 * shell is not rendered, so the transition parks forever and the account sees a blank page. That is why
 * an embedded-bundle entry navigates directly and lets the embedded-app history bridge resolve it.
 *
 * The usual reason to prefer `SafeNavigate` — that a plain navigate strands a booted embedded instance
 * on its old route — does not apply here, because the gate unmounts that instance rather than
 * navigating around it.
 *
 * A Hydra-owned entry URL has a null bundle, never parks, and keeps `SafeNavigate` so the transition
 * still goes through the navigation-guard machinery. Same distinction, and the same reason, as
 * `LastWorkspaceResolver` in -route-helpers.tsx (CON-7180).
 */
function gateRedirect(to: string, entryBundle: AppBundle | null) {
  if (entryBundle != null) {
    return <Navigate to={to} replace={true} />;
  }
  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

type AppEntryGateProps = {
  children: ReactNode;
};

/** Runs {@link appEntryGateDestination} before any authenticated page renders. */
function AppEntryGate({ children }: AppEntryGateProps) {
  // Read the store rather than `useAuth`, which throws when the stores are not yet populated. This
  // gate sits above the layout that waits for auth, so it can and does render first; with a null
  // account every check falls through and the layout handles its own loading, then the subscription
  // re-renders once the account lands.
  const account = useStore(accountStore);
  const location = useLocation();
  const hasRevalidated = useAppEntryRevalidation(location.pathname);

  const destination = appEntryGateDestination(account, location.pathname, location.searchStr);
  if (destination == null) {
    return children;
  }

  // Hold rather than strand. Every destination below unmounts the authenticated layout, and with it the
  // only query that keeps `accountStore` current — so a redirect decided on a stale snapshot can never be
  // corrected without a full page load. Waiting out the entry revalidation (see `useAppEntryRevalidation`)
  // costs a gated account one request; the happy path returns above and never reaches this.
  if (!hasRevalidated) {
    return <PageLoader />;
  }

  // Which app serves the URL the account arrived on — not the one it is being sent to. Derived here
  // rather than through `useActiveBundle` so this does not depend on the context provider sitting
  // above this gate. Re-derives when the account lands, because ownership is flag-gated.
  return gateRedirect(destination, getAppBundleFromUrl(location.pathname, account));
}

export function AuthenticatedLayout() {
  return (
    <AppEntryGate>
      <Authenticated />
    </AppEntryGate>
  );
}

export function WorkspaceRoot() {
  const location = useLocation();
  // Derive the workspace id from the pathname, not route params: switching
  // workspaces within the same app stays in the same bundle, which the
  // embedded-app history bridge syncs without a TanStack Router load. Route
  // matches/params go stale, so an effect keyed on params would never re-run
  // and the workspace-transition cache clear would silently never fire on the
  // most common switch path (CON-7180).
  const wsId = parseWorkspaceIdParam(getWorkspaceIdParam(location.pathname));
  const qc = useQueryClient();
  useEffect(() => {
    // A null id means the workspace segment is unresolved — the `/last`
    // sentinel before LastWorkspaceResolver redirects, or a non-numeric id.
    // Don't treat that as leaving the workspace: it would clear the current
    // workspace's cache only to immediately re-enter the same workspace once
    // `last` resolves. Wait for a real id so `previousWorkspaceId` always
    // tracks the last real workspace.
    if (wsId == null) return;
    handleWorkspaceTransition(qc, wsId);
  }, [qc, wsId]);
  return (
    <>
      <Outlet />
      <Suspense>
        <AssetLibrary />
      </Suspense>
      <Suspense>
        <AddonPurchaseBridge />
      </Suspense>
    </>
  );
}

function parseWorkspaceIdParam(workspaceId: string | null | undefined): number | null {
  if (workspaceId == null) return null;
  const n = Number(workspaceId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function DesignStudioRoute() {
  return (
    <LastWorkspaceResolver>
      <DesignStudioPage />
    </LastWorkspaceResolver>
  );
}

export function ThemedNotFoundRoute() {
  return (
    <ThemeContextProvider>
      <NotFoundPage />
    </ThemeContextProvider>
  );
}
